import { SubmissionTrackerResponseDto } from '../types/api';

/**
 * Plain-text export of ONLY the missing submissions from a submission
 * tracker response — forms that are fully submitted, or that have no
 * expected submitters at all, are omitted entirely rather than listed as
 * "0 missing" noise. One block per form with missing submissions, one line
 * per submitter, candidates listed as "#N (team T)" so a submitter whose
 * missing candidates span more than one team (possible for interviewers,
 * who aren't scoped to a single team) isn't ambiguous.
 */
export function buildMissingSubmissionsText(tracker: SubmissionTrackerResponseDto): string {
  const formsWithMissing = tracker.forms.filter(f => f.missingBySubmitter.length > 0);

  const header = `${tracker.cycleDisplayName} — Missing Form Submissions`;
  const generatedAt = `Generated ${new Date().toLocaleString()}`;

  if (formsWithMissing.length === 0) {
    return `${header}\n${generatedAt}\n\nAll expected submissions are complete.`;
  }

  const blocks = formsWithMissing.map(form => {
    const lines = form.missingBySubmitter.map(submitter => {
      const candidates = submitter.missingCandidates
        .map(c => `#${c.candidateNumber} (team ${c.teamNumber})`)
        .join(', ');
      return `  ${submitter.userName}: ${candidates}`;
    });
    return `${form.formName} (${form.submittedCount}/${form.expectedCount} submitted)\n${lines.join('\n')}`;
  });

  return [header, generatedAt, '', ...blocks].join('\n\n');
}

export function downloadText(text: string, filename: string): void {
  const bom = '﻿'; // UTF-8 BOM so Hebrew renders correctly in Notepad et al.
  const blob = new Blob([bom + text], { type: 'text/plain;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}
